// Import necessary Firebase functions
import { initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app); // ✅ Firebase Authentication
export const db = getFirestore(app); // ✅ Firestore Database
export const storage = getStorage(app); // ✅ Firebase Storage
export const analytics = getAnalytics(app); // Optional Analytics
export const functions = getFunctions(app); // ✅ Cloud Functions

const isLocalDevelopment =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

if (isLocalDevelopment) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099");
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}

// Fix the "missing initial state" error by setting persistence to LOCAL
// This ensures auth state persists even if sessionStorage is cleared
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log(
      "Firebase persistence set to LOCAL - this should fix the missing initial state issue"
    );
  })
  .catch((error) => {
    console.error("Error setting persistence:", error);
  });

// Add a function to handle problematic auth states
const clearAuthPendingRedirects = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      // Clear any problematic auth states in localStorage
      if (localStorage.getItem("firebase:pendingRedirect") === "true") {
        localStorage.removeItem("firebase:pendingRedirect");

        // Also check for and clear other potentially problematic auth state items
        Object.keys(localStorage).forEach((key) => {
          if (
            key.startsWith("firebase:authEvent") ||
            key.startsWith("firebaseui")
          ) {
            localStorage.removeItem(key);
          }
        });

        console.log("Cleared pending redirect states");
      }
    }
  } catch (e) {
    console.error("Error clearing auth states:", e);
  }
};

// Run the cleanup on page load
if (typeof window !== "undefined") {
  window.addEventListener("load", clearAuthPendingRedirects);
}

// NEW: Add these functions to help recover from auth state issues
export const clearAuthState = () => {
  try {
    // Clear all auth-related session storage items
    if (typeof window !== 'undefined' && window.sessionStorage) {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('firebase:auth') || key.startsWith('firebaseui:'))) {
          console.log('Clearing session storage key:', key);
          sessionStorage.removeItem(key);
        }
      }
    }
    
    // Clear any auth-related localStorage items
    if (typeof window !== 'undefined' && window.localStorage) {
      const authKeys = [
        'firebase:pendingRedirect',
        'firebase:authEvent',
        'upgradeUserId',
        'paymentPending',
        'paymentHandled',
        'paymentSessionId',
        'paymentVerified'
      ];
      
      authKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log('Clearing localStorage key:', key);
          localStorage.removeItem(key);
        }
      });
    }
    
    // Force firebase auth state to refresh
    auth.signOut().catch(err => console.error("Error in cleanup signout:", err));
    
    // Reload page to start fresh
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  } catch (error) {
    console.error("Error clearing auth state:", error);
  }
};

// Add a recovery button to the UI for users who get stuck
export const addAuthRecoveryButton = () => {
  if (typeof document !== 'undefined' && !document.getElementById('auth-recovery-btn')) {
    const btn = document.createElement('button');
    btn.id = 'auth-recovery-btn';
    btn.innerText = 'Fix Authentication';
    btn.style.position = 'fixed';
    btn.style.bottom = '20px';
    btn.style.right = '20px';
    btn.style.zIndex = '9999';
    btn.style.padding = '10px 15px';
    btn.style.background = '#ff5722';
    btn.style.color = 'white';
    btn.style.border = 'none';
    btn.style.borderRadius = '4px';
    btn.style.cursor = 'pointer';
    btn.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
    btn.style.fontFamily = 'Arial, sans-serif';
    btn.style.fontSize = '14px';
    
    btn.onclick = clearAuthState;
    
    document.body.appendChild(btn);
    console.log('Added auth recovery button');
  }
};

// Set the auth emulator domain to match the current hostname
if (typeof window !== 'undefined') {
  // Detect which domain we're on and set authDomain accordingly
  const currentHostname = window.location.hostname;
  
  // Check conditions that suggest auth issues
  const checkForAuthIssues = () => {
    const hasAuthIssues = 
      // More than 10 items in sessionStorage might suggest an auth loop
      (sessionStorage && sessionStorage.length > 10) || 
      // Pending redirect state can cause problems
      localStorage.getItem('firebase:pendingRedirect') === 'true' ||
      // The error message we're seeing
      document.body.innerText.includes('missing initial state');
    
    if (hasAuthIssues) {
      console.log('Detected potential auth issues, adding recovery button');
      addAuthRecoveryButton();
    }
  };
  
  // Run checks after page is fully loaded
  window.addEventListener('load', () => {
    // Delay slightly to ensure we catch any auth errors
    setTimeout(checkForAuthIssues, 2000);
  });
  
  // Special handling for custom domain
  if (currentHostname === 'linkist.se' || currentHostname === 'www.linkist.se') {
    // For custom domain, we need to ensure cookies work properly
    document.cookie = "authDomain=linkist.se; path=/; SameSite=None; Secure";
    
    // Set a session flag to indicate we're on custom domain
    sessionStorage.setItem('usingCustomDomain', 'true');
  }
}
